// AI hooks — useAsk (SSE streaming), useInsights, useSuggestions

import { useState, useCallback } from "react";
import { usePolling } from "./useDashboard";
import { apiFetch } from "../lib/apiFetch";
import type { InsightCard } from "../../../worker/ai/types";

// ── Shared types ──────────────────────────────────────────────────────────────

export interface StreamToken {
  type: "token";
  text: string;
}

export interface StreamDone {
  type: "done";
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  latency_ms: number;
  model: string;
  confidence: string;
}

export interface StreamError {
  type: "error";
  message: string;
}

export type StreamEvent = StreamToken | StreamDone | StreamError;

export interface AskState {
  isStreaming: boolean;
  answer: string;
  confidence: string;
  model: string;
  error: string | null;
  rateLimitRemaining: number;
  tokensUsed: { in: number; out: number } | null;
  queryId: string;
  sessionId: string;
  citations: Array<{ docIdPrefix: string; chunkIndex: number }>;
}

// ── useAsk ────────────────────────────────────────────────────────────────────

const INITIAL_ASK_STATE: AskState = {
  isStreaming: false,
  answer: "",
  confidence: "",
  model: "",
  error: null,
  rateLimitRemaining: 5,
  tokensUsed: null,
  queryId: "",
  sessionId: "",
  citations: [],
};

const CITATION_RE = /\[([a-f0-9]{6,16})#(\d{1,5})\]/g;

function extractCitations(
  text: string,
): Array<{ docIdPrefix: string; chunkIndex: number }> {
  const seen = new Set<string>();
  const out: Array<{ docIdPrefix: string; chunkIndex: number }> = [];
  for (const m of text.matchAll(CITATION_RE)) {
    const key = `${m[1]}#${m[2]}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ docIdPrefix: m[1], chunkIndex: Number(m[2]) });
  }
  return out;
}

/**
 * @param endpoint defaults to /api/ask. Set to /api/ask/board for the
 *   board-facing surface (board_member+ only, governance-tuned prompt,
 *   tighter retriever kind filter). DATA-73b — referenced from BodBrief.
 */
export function useAsk(endpoint: "/api/ask" | "/api/ask/board" = "/api/ask") {
  const [state, setState] = useState<AskState>(INITIAL_ASK_STATE);

  const ask = useCallback(
    async (question: string, pageContext?: string, sessionIdHint?: string) => {
      setState({ ...INITIAL_ASK_STATE, isStreaming: true });

      let rateLimitRemaining = 5;
      let queryId = "";
      let sessionId = "";

      try {
        const res = await apiFetch(endpoint, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question,
            context: pageContext,
            sessionId: sessionIdHint,
          }),
        });

        // Read rate limit + id headers before consuming body
        const rlHeader = res.headers.get("X-RateLimit-Remaining");
        if (rlHeader) rateLimitRemaining = parseInt(rlHeader, 10);
        queryId = res.headers.get("X-Query-Id") ?? "";
        sessionId = res.headers.get("X-Session-Id") ?? "";

        if (!res.ok) {
          const err = (await res.json()) as { error?: string; code?: string };
          setState({
            ...INITIAL_ASK_STATE,
            error: err.error ?? `Request failed (${res.status})`,
            rateLimitRemaining,
            queryId,
            sessionId,
          });
          return;
        }

        if (!res.body) {
          setState({
            ...INITIAL_ASK_STATE,
            error: "No response body",
            rateLimitRemaining,
            queryId,
            sessionId,
          });
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let fullAnswer = "";
        let confidence = "";
        let model = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          let eventType = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              const dataStr = line.slice(6).trim();
              try {
                const data = JSON.parse(dataStr) as Record<string, unknown>;

                if (eventType === "token" && typeof data["text"] === "string") {
                  fullAnswer += data["text"] as string;
                  setState((prev) => ({
                    ...prev,
                    answer: fullAnswer,
                    rateLimitRemaining,
                  }));
                } else if (eventType === "done") {
                  confidence = (data["confidence"] as string | undefined) ?? "";
                  model = (data["model"] as string | undefined) ?? "";
                  const tokensIn =
                    (data["tokens_in"] as number | undefined) ?? 0;
                  const tokensOut =
                    (data["tokens_out"] as number | undefined) ?? 0;
                  setState({
                    isStreaming: false,
                    answer: fullAnswer,
                    confidence,
                    model,
                    error: null,
                    rateLimitRemaining,
                    tokensUsed: { in: tokensIn, out: tokensOut },
                    queryId,
                    sessionId,
                    citations: extractCitations(fullAnswer),
                  });
                } else if (eventType === "error") {
                  setState({
                    ...INITIAL_ASK_STATE,
                    error:
                      (data["message"] as string | undefined) ?? "AI error",
                    rateLimitRemaining,
                  });
                }
              } catch {
                // ignore parse errors for partial chunks
              }
              eventType = "";
            }
          }
        }
      } catch (err) {
        setState({
          ...INITIAL_ASK_STATE,
          error: err instanceof Error ? err.message : "Request failed",
          rateLimitRemaining,
          queryId,
          sessionId,
        });
      }
    },
    [endpoint],
  );

  const sendFeedback = useCallback(
    async (
      queryId: string,
      rating: -1 | 0 | 1,
      reasons: string[] = [],
      freeText = "",
    ): Promise<boolean> => {
      try {
        const r = await apiFetch("/api/ask/feedback", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query_id: queryId,
            rating,
            reason_categories: reasons,
            free_text: freeText || undefined,
          }),
        });
        return r.ok;
      } catch {
        return false;
      }
    },
    [],
  );

  const reset = useCallback(() => setState(INITIAL_ASK_STATE), []);

  return { ...state, ask, reset, sendFeedback };
}

// ── Citation lookup ───────────────────────────────────────────────────────────

export interface CitationDetails {
  chunk_id: string;
  doc_id: string;
  chunk_index: number;
  section_path: string | null;
  text: string;
  source_path: string;
  source_date: string | null;
  kind: string;
  title: string;
}

export async function fetchCitationByChunkId(
  chunkId: string,
): Promise<CitationDetails | null> {
  try {
    const r = await apiFetch(
      `/api/ask/citation/${encodeURIComponent(chunkId)}`,
      {
        credentials: "same-origin",
      },
    );
    if (!r.ok) return null;
    return (await r.json()) as CitationDetails;
  } catch {
    return null;
  }
}

// ── Memory (BOD corpus) search ────────────────────────────────────────────────

export interface MemoryResult {
  chunk_id: string;
  doc_id: string;
  chunk_index: number;
  title: string;
  kind: string;
  source_date: string | null;
  section_path: string | null;
  snippet: string;
  score: number;
}

export async function searchMemory(
  query: string,
  kinds?: string[],
): Promise<MemoryResult[]> {
  const params = new URLSearchParams({ q: query });
  if (kinds && kinds.length > 0) params.set("kind", kinds.join(","));
  const r = await apiFetch(`/api/memory/search?${params.toString()}`, {
    credentials: "same-origin",
  });
  if (!r.ok) return [];
  const body = (await r.json()) as { results: MemoryResult[] };
  return body.results;
}

// ── Viability ─────────────────────────────────────────────────────────────────

export interface ViabilityScenario {
  scenario: string;
  projection: {
    yearly_headcount?: Array<{
      year: number;
      active_members: number;
      ci_low?: number;
      ci_high?: number;
    }>;
    milestones?: Array<{ year: number; event: string }>;
  } | null;
  narrative: string | null;
  generated_at: string;
  model_used: string | null;
}

export function useViability() {
  return usePolling<{ scenarios: ViabilityScenario[] }>(
    "/api/ai/viability",
    30 * 60 * 1000,
  );
}

// ── useInsights ───────────────────────────────────────────────────────────────

export interface InsightsResponse {
  insights: InsightCard[];
}

export function useInsights(page?: string) {
  const url = page
    ? `/api/ai/insights?page=${encodeURIComponent(page)}`
    : "/api/ai/insights";
  return usePolling<InsightsResponse>(url, 300_000); // refresh every 5 min
}

// ── useSuggestions ────────────────────────────────────────────────────────────

export interface SuggestionsResponse {
  page: string;
  questions: string[];
}

export function useSuggestions(page = "overview") {
  const url = `/api/ai/suggestions?page=${encodeURIComponent(page)}`;
  return usePolling<SuggestionsResponse>(url, 3_600_000); // refresh hourly
}

// ── useRiskSummary ────────────────────────────────────────────────────────────

export interface RiskSummaryResponse {
  critical: number;
  high: number;
  moderate: number;
  low: number;
  total: number;
  computedAt: string | null;
}

export function useRiskSummary() {
  return usePolling<RiskSummaryResponse>("/api/ai/risk-summary", 300_000); // refresh every 5 min
}

// ── useRiskScores ─────────────────────────────────────────────────────────────

export interface RiskSignal {
  name: string;
  weight: number;
}

export interface RiskScoreRow {
  member_id: string; // airtable_id from dash_members
  score: number;
  risk_level: string;
  top_signals: string[];
  signals: RiskSignal[];
  computed_at: string;
}

export interface RiskScoresResponse {
  scores: RiskScoreRow[];
  count: number;
}

export function useRiskScores() {
  return usePolling<RiskScoresResponse>("/api/ai/risk-scores", 300_000); // admin-only endpoint
}

// ── usePipelineStalls ─────────────────────────────────────────────────────────

export interface PipelineStallRow {
  applicant_id: number;
  days_in_stage: number | null;
  stall_flag: number;
  stall_stage: string | null;
  computed_at: string;
}

export interface PipelineStallsResponse {
  stalls: PipelineStallRow[];
  count: number;
}

export function usePipelineStalls() {
  return usePolling<PipelineStallsResponse>("/api/ai/pipeline-stalls", 300_000);
}
